import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { IndexedStorageService } from './storage.service';
import { environment } from '../../environments/environment';

import { IReviewQuestion } from '../pages/evaluation-review/questionari/questionari';
import { risk_source_model } from '../pages/evaluation-element/evaluation-element-page';
import { IModelReview, IRiskSourceRisk, risk_cons_t, risk_prob_t } from '../pages/evaluation-review/evaluation-review-page';
import { TranslateService } from '@ngx-translate/core';

import {
  Observable, Subject, mergeMap, from,
  tap, timer,retry, catchError,
  switchMap, map, BehaviorSubject
} from 'rxjs';

import { Device, DeviceId } from '@capacitor/device';
import { IRiskPayload } from '../shared/ui/risk-review-card';

export const CREDS_KEY = "_auth_";
const ep = (path: string) => `${environment.host}/api/${path}/`;


// L'API retorna les dades embolicades en una estructura constant:
export interface IDataWrap<T> {
  status: number,
  success: boolean,
  message: string,
  metadata?: {[k:string]:string},
  data: T,
}
// aquesta funció auxiliar n'obté les dades crues
// amb el seu corresponent tipus
const unwrap = <T>()=>map((wrap:IDataWrap<T>)=>wrap.data);

export interface IAuthCredentials {
  access_token: string,
  refresh_token: string
}


@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private _credentials?: IAuthCredentials;
  private _waiting: boolean = false;
  private _sync$ = new Subject<void>();
  private _device_id: DeviceId = { identifier: 'b6:05:34:1c:83:6a' }; // <- s'sinicialitza al constructor!

  private _session = new Subject<boolean>()
  public session$ = this._session.asObservable();

  private readonly _busy = new BehaviorSubject<number>(0);
  get busy$() {
    return this._busy.pipe(map(count=>!!count))
  }
  private readonly _errors = new Subject<IDataWrap<undefined>>();
  get error$() {
    return this._errors.asObservable();
  }
  

  // Observable que emet les credencials
  // disponibles en el moment de la subscripció i es completa.
  // En cas de no haver-hi credencials, llença un error custom 420
  private get _authCred$() {
    return new Observable<IAuthCredentials>((observer) => {
      if (!!this._credentials) {
        observer.next(this._credentials);
        observer.complete();
      } else {
        observer.error(new HttpErrorResponse({
          error: "No hi ha credencials",
          status: 420,
          statusText: "Must log-in"
        }));
      }
    });
  }

  backoffRetry<T>({ count, delay } = { count: 5, delay: 1000 }) {
    return (obs$: Observable<T>) => obs$.pipe(
      retry({
        count,
        delay: (_err, retryIndex) => {
          //console.log(`DEBUG ${this.constructor.name} exponential backoff`, _err);
          const d = Math.pow(2, retryIndex - 1) * delay;
          return timer(d);
        }
      })
    );
  }

  // Notifica  globalment l'estat de l'api mitjançant els
  // Observables this.busy$, per mostrar un "loading", 
  // i this.error$ per la gestió d'errors. 
  busyWatch<T>() {
    return (observable: Observable<T>) => new Observable<T>(observer => {
      this._busy.next(this._busy.value + 1);
      let clean = false;
      const _subs = observable.subscribe({
        next: (value) => observer.next(value),
        error: (err)=>{
          this._busy.next(this._busy.value - 1);
          clean = true;
          this._errors.next(err as IDataWrap<undefined>);
          observer.error(err);
        },
        complete: () => {
          this._busy.next(this._busy.value - 1);
          clean = true;
          observer.complete();
        }
      });

      // the return value is the teardown function,
      // which will be invoked when the new
      // Observable is unsubscribed from.
      return () => {
        // ens assegurem que el loading es destrueix.
        if (!clean) this._busy.next(this._busy.value - 1);
        _subs.unsubscribe();
      }
    });
  }

  /**
   * Composa peticions HTTP a partir de les IAuthCredentials llegides en el moment de la susbscripció.
   * Captura els errors 401 (unauthorized) per refrescar les credencials i reintentar la petició amb un nou header.
   */
  private authManager<T>(projfn: (opts: IAuthCredentials) => Observable<T>) {
    // Mostra el LoginComponent i retorna un Observable<any> que es completa
    // en tancar-se el modal, quan les credencials ja s'han refrescat.
    const refreshAuth = () => {
      const { refresh_token } = this._credentials!;
      if (this._waiting) return this._sync$.asObservable();
      this._waiting = true;
      const bdy = new FormData();
      return this.http.post<IDataWrap<{ access_token: string }>>(ep('auth/refresh-token'), { refresh_token }, {
        headers: { 'Content-Aype': 'application/json' }
      }).pipe(
        unwrap(),
        tap({
          next: (nxt) => {
            //console.log(`DEBUG authManager, refreshAuth responds`, nxt);
            if (!!this._credentials) {
              this._credentials.access_token = nxt.access_token;
              this.storage.dbSet(CREDS_KEY, this._credentials).subscribe({
                error:(err)=>console.log('storing creds', err)
            });
            } else throw new Error('wtf: no credentials?');
            this._sync$.next();
          },
          error: err => {
            console.log('ApiService.authManager', err);
            this._waiting = false;
            delete this._credentials;
            this.storage.dbRemove(CREDS_KEY).subscribe();
            this.router.navigateByUrl('/');
            this._sync$.next();
          },
          complete: () => { this._waiting = false; }
        })
      );
    }
    // Obté un Observable auxiliar que rellança tots els errors rebuts excepte el 401.
    // Si l'error és el 401, retornarà un observable que indiqui el moment en que hi ha
    // disponibles noves credencials, per poder reintentar la petició.
    const checkAuthDelay = (err: HttpErrorResponse, retryCount: number) => {
      if (err.status == 420) return from(this.router.navigateByUrl('/'));
      if (err.status != 401) throw err;
      return refreshAuth();
    }

    return this._authCred$.pipe(
      mergeMap(projfn),
      retry({ count: 1, delay: checkAuthDelay }), // <- en fallar les credencials amb error 401, espera a que s'hagin renovat i reintenta 1 cop)
      tap({
        // Si desprès de refrescar el token continua fallant amb 401, redirigeix (a login)
        error: (err) => {
          if (err.status == 401) {
            console.log('error en refresc de token', err)
            this.storage.dbRemove(CREDS_KEY).subscribe();
            this.router.navigateByUrl('/');
          }
        }
      })
    );
  }

  constructor(
    private http: HttpClient,
    private storage: IndexedStorageService,
    private translate: TranslateService,
    private router: Router,
  ) {
    Device.getId().then(
      (duid) => this._device_id = duid
    ).catch(err=>console.log(`ApiService getting DeviceId:`, err));

    this.storage.dbGet<IAuthCredentials|undefined>(CREDS_KEY).subscribe({
      next:(sauth)=>{
        if (!!sauth) {
          this._credentials = sauth;
          this._session.next(true);
        }
      },
      error:err=>console.log('ApiService failed reading storage', err)
    })
  }


  /** Inicia sessió i refresca les credencials */
  login(user: string, pass?: string, code?: string) {
    const { identifier } = this._device_id;

    const password = btoa(pass!);
    const body = { user, password, device_id:identifier };

    return this.http.post<IDataWrap<{ code: string }>>(ep('auth/login'), body, {
      headers: { 'Content-Type':'application/json'}
    }).pipe(
      unwrap(),
      switchMap(({ code })=>{
        return this.http.post<IDataWrap<IAuthCredentials>>(
          ep('auth/token'), { code }, { 
            headers: { "Content-Type": "application/json" }, 
          }
        ).pipe(unwrap());
      }),
      // Mapejo l'error 400 BAD REQUEST amb el missatge d'error retornat per l'endpoint,
      // en la resta d'errades no hi inteferim
      catchError((err: HttpErrorResponse) => {
        //console.log(`DEBUG ${this.constructor.name} caught error:`, err);
        const msg = err.headers?.get('x-previnapp-message') || err.message
        console.log('login caught message', msg, {err});
        if (err.status == 400) throw new Error(msg);
        else throw err;
      }),
      tap({
        next: (creds) => {
          this._credentials = creds;
          this.storage.dbSet(CREDS_KEY, creds).subscribe({
            error:err=>console.log('saving credentials', err)
          });
          this._session.next(true);
        }
      }),
      this.busyWatch()
    )
  }

  logout() {
    const cleanup = ()=>{
      console.log("cleaning caché & credentials")
      const uid = this.storage.dbRemove(CREDS_KEY).subscribe();
      this._session.next(false);
    }
    return this.authManager(({ access_token }) => this.http.post<void>(
      ep('auth/logout'), null, { headers: { "Authorization": `Bearer ${access_token}` } }
    )).pipe(tap({
      error: (err)=>{console.log('logout error', err); cleanup() },
      complete: cleanup
    }));
  }

  languages() {
    return this.authManager(({ access_token }) => this.http.get<IDataWrap<any[]>>(
      ep('languages'),
      { headers: { "Authorization": `Bearer ${access_token}` } }
    )).pipe(
      unwrap(),
      // converteix la llista retornada a un diccionari id->nom
      // en cas que no retorni un arrai, retorna {}
      map(nxt => Array.isArray(nxt) ? nxt.reduce(
        (a, b) => { a[`${b.language_id}`] = b.language_name; return a },
        {} as { [k: string]: string }
      ) : {})
    );
  }

  // obté la informació de l'usuari (nom i cognoms)
  public userInfo() {
    return this.authManager(({ access_token }) => this.http.get<IDataWrap<any[]>>(
      ep('user-info'),
      { headers: { "Authorization": `Bearer ${access_token}` } }
    )).pipe(
      unwrap(),
      map((nxt) => nxt[0])
    );
  }

  
  assessmentSources(assessment_id: number, previnapp_ia=false) {
    return this.authManager(({ access_token }) => this.http.get<IDataWrap<{
      risk_source_type_id: number,
      risk_source_id: number,
      risk_name: string
    }[]>>(
      ep('assessment-sources'), {
      params: { assessment_id, previnapp_ia }, headers: { "Authorization": `Bearer ${access_token}` } 
    }
    )).pipe(
      unwrap(),
      this.busyWatch()
    );
  }

  workplaces(company_id:string) {
    return this.authManager(({access_token})=>this.http.get<IDataWrap<any[]>>(
      ep('companies/workplaces'), {
        params: { company_id }, 
        headers: { "Authorization": `Bearer ${access_token}` } 
      }
    )).pipe(
      unwrap(),
      this.busyWatch()
    )
  }

  companyJobs(company_id:string, prevention=true) {
    return this.authManager(({access_token})=>this.http.get<IDataWrap<{
      company_job_id:number, company_job_name:string, 
      start_date:string, end_date: string|null}[]>>(
      ep('companies/jobs') , {
        params: { company_id, prevention }, headers: { "Authorization": `Bearer ${access_token}` } 
      }
    )).pipe(
      unwrap(),
      this.busyWatch()
    )
  }

  companyJobMachines(company_id: string, company_job_id:number) {
    return this.authManager(({access_token})=>this.http.get<IDataWrap<any[]>>(
      ep('companies/job-machines') , {
        params: { company_id, company_job_id }, headers: { "Authorization": `Bearer ${access_token}` } 
      }
    )).pipe(
      unwrap(),
      this.busyWatch()
    )
  }

  
  builderCompanies() {
     return this.authManager(({access_token})=>this.http.get<IDataWrap<Array<{
      company_id:string, company_name: string, company_groups:Array<{
        company_group_id: number, company_group_name: string
      }>
     }>>>(
      ep('companies'), { headers: { "Authorization": `Bearer ${access_token}` } }
     )).pipe(
      unwrap(), this.busyWatch()
     );
  }
  builderCompanyDetail(company_id:string) {
    return this.authManager(({access_token})=>this.http.get<IDataWrap<{
      language_id: string,
      assessments: Array<{
        assessment_id: number,
        assessment_description: string,
        start_date:number,
        status_id:number,
        user_id_technician:string,
        workplace_id: number,
        workplace_name: string
      }>
    }>>(
      ep('companies/details'), {
        params: { company_id }, headers: { "Authorization": `Bearer ${access_token}` }
      }
     )).pipe(
      unwrap(),
      this.busyWatch()
     );
  }

  
  builderQuestionnair(assessment_id:number, company_id:string, risk_source_type_id:number, risk_source_id:string) {
    return this.authManager(({access_token})=>this.http.get<IDataWrap<IReviewQuestion[]>>(
      ep('risk-source-questions'), {
        params: { assessment_id, company_id, risk_source_id, risk_source_type_id }, headers: { "Authorization": `Bearer ${access_token}` } 
      }
    )).pipe(
      unwrap(),
      this.busyWatch()
    )
  }

  questionnairResponses(assessment_id:number, company_id:string, risk_source_type_id:number, risk_source_id:string) {
    return this.authManager(({access_token})=>this.http.get<IDataWrap<any[]>>(
      ep('answered-risk-source-questions'), {
        params: { assessment_id, company_id, risk_source_id, risk_source_type_id }, headers: { "Authorization": `Bearer ${access_token}` } 
      }
    )).pipe(
      unwrap(),
      this.busyWatch()
    )
  }

  deleteRiskSourceRisk(
    assessment_id:number, company_id:string, risk_source_type_id:number, risk_source_id:number, company_risk_id:number
  ) {
    return this.authManager(({access_token})=>this.http.delete<IDataWrap<any[]>>(
      ep('delete-risk-source-risk'), {
        params: { assessment_id, company_id, risk_source_id, risk_source_type_id, company_risk_id }, 
        headers: { "Authorization": `Bearer ${access_token}` } 
      }
    )).pipe(
      unwrap(),
      this.busyWatch()
    )
  }

  postRiskSourceRisk(
    assessment_id:number, company_id:string, risk_source_type_id:number, risk_source_id:number, 
    riscos:IRiskPayload[]
  ) {
    const payload = new FormData();
    riscos.forEach(risc=>payload.append('company_risks[]',JSON.stringify(risc)));
    return this.authManager(({access_token})=>this.http.post<IDataWrap<any[]>>(
      ep('risk-source-risks/upload'), payload, {
        params: { assessment_id, company_id, risk_source_id, risk_source_type_id }, 
        headers: { "Authorization": `Bearer ${access_token}` } 
      }
    )).pipe(
      unwrap(),
      this.busyWatch()
    )
  }

  updateRiskSourceRisk(
    assessment_id:number, company_id:string, risk_source_type_id:number, risk_source_id:number, 
    risc:IRiskPayload
  ) {
    const payload = new FormData();
    payload.append('company_risk', JSON.stringify(risc));
    return this.authManager(({access_token})=>this.http.post<IDataWrap<any[]>>(
      ep('risk-source-risks/update'), payload, {
        params: { assessment_id, company_id, risk_source_id, risk_source_type_id }, 
        headers: { "Authorization": `Bearer ${access_token}` } 
      }
    )).pipe(
      unwrap(),
      this.busyWatch()
    )
  }

  postRiskSourceRiskDocument(
    assessment_id:number, company_id:string, risk_source_type_id:number, risk_source_id:number, 
    data_collection_question_id:number, base64thumbnail:string
  ) {
    const payload = new FormData();
    payload.append('b64file[]', base64thumbnail );
    return this.authManager(({access_token})=>this.http.post(
      ep('risk-source-risks/document'), payload, {
        params: { assessment_id, company_id, risk_source_type_id, risk_source_id, data_collection_question_id },
        headers: { "Authorization": `Bearer ${access_token}` },
        responseType: "text"
      }
    ));
  }

  agentURLs() {
    return this.authManager(({access_token})=>this.http.get<IDataWrap<{
      id: number, url: string, type: string, enabled: boolean, 
      usage_count: number, last_used_at: string
    }[]>>(
      ep('agent-urls'), { headers: { 'Authorization':`Bearer ${access_token}`} }
    )).pipe(unwrap())
  }

  checkQuestionnair(
    assessment_id:number, 
    company_id:string, 
    risk_source_type_id:number, 
    risk_source_id:number, 
    data_collection_question_id:number, 
    value:number | string
  ) {
    return this.authManager(({access_token})=>this.http.post<IDataWrap<IReviewQuestion[]>>(
      ep('risk-source-questions/answer'), {
        assessment_id, company_id, risk_source_type_id, 
        risk_source_id, data_collection_question_id, value 
      } ,{
        headers: { "Authorization": `Bearer ${access_token}` }
      }
    )).pipe(unwrap());
  }

  getTranscribeKey() {
    return this.authManager(({access_token})=>this.http.get<IDataWrap<{transcribe_api_key:string}>>(
      ep('transcribe/api-key'), {
        headers: { "Authorization": `Bearer ${access_token}` }
      }
    )).pipe(unwrap())
  }

  avaluaFoto(foto: Blob, model:'seccions'|'llocs'|'maquines'|'quimics'='maquines') {
    const payload = new FormData();
    payload.append('image', foto);
    payload.append('language', this.translate.getCurrentLang());
    return this.agentURLs().pipe(
      //tap(urls=>console.log('model-urls', urls)),
      switchMap(urls=>{
        const modelUrl = urls.find(url=>url.type==model)!.url;
        return this.authManager(({access_token})=>this.http.post<IModelReview>(
          modelUrl, payload, //{ headers: { access_token }}
        )).pipe(
          map(resp=>{
            // Correccio: la ia ara sempre retornarà "0"|"1"|"2"
            // per als valors de risk_probability i risk_consequence,
            // i els mapejem als valors esperats sense barreges inoportunes.
            const { data } = resp;
            const { risk_assessment } = data;
            const fix_assessment = risk_assessment.map(ra=>{
              const { risk_probability, risk_consequence } = <any>ra as {[k:string]:"0"|"2"|"2"};
              const probDict = {"0":"Baja","1":"Media","2":"Alta"};
              const consDict = {"0":"Leve","1":"Mediana","2":"Grave"}
              ra.risk_probability = probDict[risk_probability] as risk_prob_t;
              ra.risk_consequence = consDict[risk_consequence] as risk_cons_t;
              return ra;
            });
            data.risk_assessment = fix_assessment;
            resp.data = data;
            return resp;
          })
        )
      }),
      this.busyWatch()
    )
  }

  getRiskSourceRisks(assessment_id:number, company_id:string, risk_source_type_id:number, risk_source_id:number) {
    return this.authManager(({access_token})=>this.http.get<IDataWrap<IRiskSourceRisk[]>>(
      ep('risk-source-risks'), {
        params: { assessment_id, company_id, risk_source_type_id, risk_source_id }, headers: {
          'Authorization': `Bearer ${access_token}`
        }
      }
    )).pipe(
      unwrap()
    )
  }

  riskSourceModels(risk_source_type_id:number, company_id:string) {
    return this.authManager(({access_token})=>this.http.get<IDataWrap<risk_source_model>>(
      ep('risk-source-models'), {
        params: { risk_source_type_id, company_id }, headers: { 'Authorization': `Bearer ${access_token}` }
      }
    )).pipe(
      unwrap(),
      this.busyWatch()
    )
  }

  /* == PER CREAR noves fonts de risc: ==

  1 Cridar a GET_RISK_SOURCES_MODELS per obtenir la llista de fonts de risc que es poden insertar
  2 GET_WORKPLACES per obtenir section_id o workplace_id (té)
  3 POST_RISK_SOURCE per publicar-la 

    # OPTIONAL PARAMETERS (form-data):
    # sections is required when -> risk_source_type_id = 3 or 5.
    # workplaces is required when -> risk_source_type_id = 7.

  */
  createRiskSource( 
    assessment_id:number, company_id:string, risk_source_type_id:number,
    risk_source_id:number, sections:Array<{section_id:number}>|undefined, 
    jobs:Array<{company_job_id:number}>|undefined, machines:Array<{company_machine_id:number}>|undefined
  ) {    
    //  IN CASE OF DOCUMENT/IMAGE INSERTION:
    //  Body Form-data -> b6file
    const payload = new FormData();
    const params:any = { assessment_id, company_id, risk_source_type_id, risk_source_id };
    if (risk_source_type_id == 7) {
      payload.append('company_machines', JSON.stringify(machines || []));
      payload.append('company_jobs', JSON.stringify(jobs || []));
    } else if (risk_source_type_id == 5) {
      payload.append('sections', JSON.stringify(sections || []));
      payload.append('company_jobs', JSON.stringify(jobs || []));
    } else {
      payload.append('sections', JSON.stringify(sections || []));
      payload.append('company_machines', JSON.stringify(machines || []));
    }
    return this.authManager(({access_token})=>this.http.post<IDataWrap<risk_source_model>>(
      ep('risk-source/create'), payload, { params, headers: { 'Authorization': `Bearer ${access_token}` }}
    )).pipe(
      unwrap(),
      this.busyWatch()
    );
  }

  riskSourceRelations(assessment_id:number, company_id:string, risk_source_type_id:number, risk_source_id:number) {
    return this.authManager(({access_token})=>this.http.get<IDataWrap<{
      company_machines?:Array<{ company_machine_id:number, name: string }>,
      sections?:Array<{ section_id: number, section_name: string }>,
      company_jobs?:Array<{ company_job_id: number, company_job_name:string }>
    }>>(
      ep('risk-source-relations'), {
        params: { assessment_id, company_id, risk_source_type_id, risk_source_id }, 
        headers: { 'Authorization': `Bearer ${access_token}` }
      }
    )).pipe(
      unwrap(),
      this.busyWatch()
    )
  }

  renameRiskSource(assessment_id:number, company_id:string, risk_source_type_id:number, risk_source_id:number, risk_source_name:string) {
    return this.authManager(({access_token})=>this.http.post(
      ep('risk-source/rename'), null, {
        params: { assessment_id, company_id, risk_source_type_id, risk_source_id, risk_source_name },
        headers: { 'Authorization': `Bearer ${access_token}` },
        responseType: 'text'
      }
    ))
  }


  riskSourceImage(assessment_id:number, company_id:string, risk_source_type_id:number, risk_source_id:number, image:string) {
    const payload = new FormData();
    payload.append('b64file', image );
    return this.authManager(({access_token})=>this.http.post(
      ep('risk-source/image'), payload, {
        params: { assessment_id, company_id, risk_source_type_id, risk_source_id },
        headers: { 'Authorization': `Bearer ${access_token}` },
        responseType: 'text'
      }
    )) 
  }

  answeredDocuments(assessment_id:number, company_id:string, risk_source_type_id:number, risk_source_id:number) {
    return this.authManager(({access_token})=>this.http.get<IDataWrap<{
        data_collection_question_id: number,
        data_collection_question_id_father: number,
        b64files: string[]
      }[]>>(
      ep('answered-risk-source-questions/documents'), {
        params: { assessment_id, company_id, risk_source_type_id, risk_source_id }, 
        headers: { 'Authorization': `Bearer ${access_token}` }
      }
    )).pipe(
      unwrap(),
      //this.busyWatch()
    )
  }

  risks() {
    return this.authManager(({access_token})=>this.http.get<IDataWrap<{risk_id:number, risk_name:string}[]>>(
      ep('risks'), { headers: { 'Authorization': `Bearer ${access_token}` } }
    )).pipe(
      unwrap(),
      this.busyWatch()
    )
  }
 
}


